import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState } from "react";

import { logger } from "c7-logger/index";

import {
  paymentMessagesAtom,
  paymentRequirementsAtom,
} from "checkout-experience/atoms/payment-atoms";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { PayRequestPayload } from "checkout-query-client/api/types";
import { usePubSub } from "checkout-query-client/hooks/use-pub-sub";
import {
  PubSubMessage,
  PubSubNamespace,
} from "checkout-query-client/publish-subcriber/types";
import { PaymentType } from "checkout-query-client/types/form-types";

import { endDatadogSession, sendCustomAction } from "datadog-loader/loader";

import {
  PayRequestErrorCode,
  PayResponse,
  PayResponse_Status,
} from "frontend-client";

interface Params {
  makePayment?: (payload: PayRequestPayload) => Promise<PayResponse>;
  onPayResult?: (result: PayResponse) => Promise<PayResponse_Status | void>;
  isPayInProgress?: boolean;
  handleRedirect?: (status: PayResponse_Status) => void;
}

const usePayInterceptor = ({
  makePayment,
  onPayResult,
  isPayInProgress,
  handleRedirect,
}: Params) => {
  const [paymentRequirements, setPaymentRequirements] = useAtom(
    paymentRequirementsAtom
  );
  const { experienceType, merchantSettings } = useChexContext();
  const [paymentData, setPaymentData] = useState<PayResponse>();
  const pubSub = usePubSub();
  const paymentMessages = useAtomValue(paymentMessagesAtom);

  useEffect(() => {
    if (experienceType === "dropInUI") {
      // check if 3ds finished successfully
      if (
        paymentMessages.threeDsCompletionMessage?.status === "STATUS_SUCCESS"
      ) {
        const { threeDs, ...remainingPaymentRequirements } =
          paymentRequirements;
        if (threeDs) setPaymentRequirements(remainingPaymentRequirements);
        pubSub.publish({
          level: "info",
          namespace: PubSubNamespace.payment,
          message: PubSubMessage.PaymentSuccess,
          payload: {
            payResponse: JSON.stringify(
              paymentMessages.threeDsCompletionMessage
            ),
          },
        });
        // This should prevent new items from being records and push any queued events
        endDatadogSession();
        // check if 3ds finished with a failure
      } else if (
        paymentMessages?.threeDsCompletionMessage?.status === "STATUS_FAILURE"
      ) {
        pubSub.publish({
          level: "info",
          namespace: PubSubNamespace.payment,
          message: PubSubMessage.PaymentUnsuccessful,
          payload: {
            payResponse: JSON.stringify(
              paymentMessages.threeDsCompletionMessage
            ),
          },
        });
      } else if (
        paymentMessages.lpmCompletionMessage?.status === "STATUS_SUCCESS"
      ) {
        const { lpm, ...remainingPaymentRequirements } = paymentRequirements;
        if (lpm) setPaymentRequirements(remainingPaymentRequirements);
        pubSub.publish({
          level: "info",
          namespace: PubSubNamespace.payment,
          message: PubSubMessage.PaymentSuccess,
          payload: {
            payResponse: JSON.stringify(paymentMessages.lpmCompletionMessage),
          },
        });
        // This should prevent new items from being records and push any queued events
        endDatadogSession();
      } else if (
        paymentMessages?.lpmCompletionMessage?.status === "STATUS_FAILURE"
      ) {
        pubSub.publish({
          level: "info",
          namespace: PubSubNamespace.payment,
          message: PubSubMessage.PaymentUnsuccessful,
          payload: {
            payResponse: JSON.stringify(paymentMessages.lpmCompletionMessage),
          },
        });
      } else if (
        paymentMessages?.lpmCompletionMessage?.status === "STATUS_PENDING"
      ) {
        pubSub.publish({
          level: "info",
          namespace: PubSubNamespace.payment,
          message: PubSubMessage.PaymentPending,
          payload: {
            payResponse: JSON.stringify(paymentMessages.lpmCompletionMessage),
          },
        });
      }
    } else if (experienceType === "hpp" || experienceType === "hppLink") {
      if (paymentData?.status === PayResponse_Status.SUCCESS) {
        handleRedirect?.(paymentData?.status);
      } else if (
        paymentMessages.lpmCompletionMessage?.status === "STATUS_PENDING"
      ) {
        handleRedirect?.(PayResponse_Status.PENDING);
      } else if (paymentRequirements.threeDs) {
        if (
          paymentMessages.threeDsCompletionMessage?.status === "STATUS_SUCCESS"
        ) {
          const { threeDs, ...remainingPaymentRequirements } =
            paymentRequirements;
          if (threeDs) setPaymentRequirements(remainingPaymentRequirements);

          if (Object.keys(remainingPaymentRequirements).length === 0) {
            handleRedirect?.(PayResponse_Status.SUCCESS);
          }
        } else if (
          paymentMessages.threeDsCompletionMessage?.status === "STATUS_FAILURE"
        ) {
          handleRedirect?.(PayResponse_Status.FAILURE);
        }
      } else if (paymentRequirements.lpm) {
        if (paymentMessages.lpmCompletionMessage?.status === "STATUS_SUCCESS") {
          const { lpm, ...remainingPaymentRequirements } = paymentRequirements;
          if (lpm) setPaymentRequirements(remainingPaymentRequirements);

          if (Object.keys(remainingPaymentRequirements).length === 0) {
            handleRedirect?.(PayResponse_Status.SUCCESS);
          }
        } else if (
          paymentMessages.lpmCompletionMessage?.status === "STATUS_FAILURE"
        ) {
          handleRedirect?.(PayResponse_Status.FAILURE);
        }
      } else if (onPayResult && paymentData) {
        void onPayResult(paymentData);
      }
    }
  }, [paymentMessages, paymentData, paymentRequirements]);

  return async (
    payload: PayRequestPayload
  ): Promise<PayResponse_Status | void> => {
    if (!payload || isPayInProgress) {
      logger.error(
        `On Pay Called, but not run.  Payload:  ${!!payload} In progress: ${JSON.stringify(isPayInProgress)}`
      );
    } else if (makePayment) {
      logger.log("Key Id", merchantSettings?.encryptionKeyId);
      sendCustomAction({
        name: "Pay Enc Info",
        context: {
          encryptionKeyId: merchantSettings?.encryptionKeyId,
          encryptionKey: merchantSettings?.encryptionKey,
        },
      });
      const result = await makePayment(payload);
      if (
        result.status === PayResponse_Status.FAILURE &&
        result.payRequestErrorCode === PayRequestErrorCode.INVALID_CVV
      ) {
        // Catch invalid CVV to allow user to fix it
      } else if (
        result.status === PayResponse_Status.FAILURE &&
        result.scaOrchestrationUrl
      ) {
        // 3ds
        setPaymentRequirements({
          ...paymentRequirements,
          threeDs: result.scaOrchestrationUrl,
        });
      } else if (
        result.status === PayResponse_Status.FAILURE &&
        result.pbbConsentUrl
      ) {
        // Pay by Bank
        setPaymentRequirements({
          ...paymentRequirements,
          payByBankConsentUrl: result.pbbConsentUrl,
        });
      } else if (
        result.status === PayResponse_Status.SUCCESS &&
        result.lpmOrchestrationUrl
      ) {
        // LPM
        setPaymentRequirements({
          ...paymentRequirements,
          lpm: result.lpmOrchestrationUrl,
        });
      } else {
        setPaymentData(result);
        if (payload.paymentType === PaymentType.applePay && onPayResult) {
          return onPayResult(result);
        }
      }
    }
  };
};

export default usePayInterceptor;
